import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// SS11 perf: the Clients/Engagements list queries are heavy (Engagements ~2-3s), so
// cache them for a few minutes. Returning to the page is instant; saves already
// invalidate these lists, and the header refresh button forces a reload.
const LIST_STALE_MS = 5 * 60 * 1000;
import { api } from './client';

export interface Me {
  id: string;
  email: string;
  name: string;
  roles: string[];
  isSuperAdmin: boolean;
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me') });

/** A row of the tb_Users access table (super-admin Manage Users page). */
export interface AppUser {
  ID: number;
  Name: string;
  EMail: string;
  Role: string | null;
}
export type UserInput = { name: string; email: string; role: string };

export const useUsers = () =>
  useQuery({ queryKey: ['users'], queryFn: () => api.get<AppUser[]>('/users') });

export const useSaveUser = () => {
  const qc = useQueryClient();
  return useMutation({
    // originalEmail set => update (PUT); undefined => create (POST).
    mutationFn: (args: { originalEmail?: string; body: UserInput }) =>
      args.originalEmail
        ? api.put(`/users/${encodeURIComponent(args.originalEmail)}`, args.body)
        : api.post('/users', args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
};

export const useDeleteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.del(`/users/${encodeURIComponent(email)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
};

export const useCounts = () =>
  useQuery({
    queryKey: ['counts'],
    queryFn: () => api.get<{ pendingClients: number; pendingEngagements: number }>('/dashboard/counts'),
  });

export interface Metrics {
  totalClients: number;
  totalEngagements: number;
  clientsCreatedViaApp: number;
  engagementsCreatedViaApp: number;
  pendingClients: number;
  pendingEngagements: number;
  completedClients: number;
  completedEngagements: number;
  avgClientCompletionHours: number | null;
  avgClientCompletionDays: number | null;
  avgEngagementCompletionHours: number | null;
  avgEngagementCompletionDays: number | null;
}

export const useMetrics = () =>
  useQuery({
    queryKey: ['metrics'],
    queryFn: () => api.get<Metrics>('/metrics'),
    staleTime: 1000 * 60 * 5,
  });

export interface EtlStatus {
  inProgress: boolean;
  tables: { TableName: string; LatestRunStatus: string }[];
}

/** Maconomy load status. Power Apps polls this every 3s and blocks the data-entry
 *  screens while a load runs; we poll a little less aggressively. */
export const useEtlStatus = () =>
  useQuery({
    queryKey: ['etl-status'],
    queryFn: () => api.get<EtlStatus>('/etl-status'),
    refetchInterval: 15000,
  });

export const useLookup = (name: string, enabled = true) =>
  useQuery({
    queryKey: ['lookup', name],
    queryFn: () => api.get<Record<string, unknown>[]>(`/lookups/${name}`),
    staleTime: 1000 * 60 * 30,
    enabled,
  });

export const useClients = (grouping?: string, search?: string) =>
  useQuery({
    queryKey: ['clients', grouping ?? '', search ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (search) qs.set('search', search);
      return api.get<Record<string, unknown>[]>(`/clients?${qs.toString()}`);
    },
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  });

/** A row from the client pickers (vw_ClientID_Master / vw_ClientID). */
export interface ClientPickerRow {
  ClientID: string;
  ClientName: string;
  ClientGrouping: string;
  DisplayText?: string;      // master picker: "ClientID - ClientName"
  ClientFullName?: string;   // all-clients picker: "ClientID-ClientName"
  // vw_ClientID also carries these — used to auto-fill the New Engagement client block.
  ClientManager?: string;
  BRCIndustry?: string;
  ClientEmail?: string;
  ContactName?: string;
  FiscalYearEndMonth?: string;
  BillingStatementEmail?: string; // defaults the Email for Monthly Statements field
}

/** Distinct "starts with" letter groupings (alpha-first) for the client pickers. */
export const useClientGroupings = () =>
  useQuery({
    queryKey: ['client-groupings'],
    queryFn: () => api.get<{ ClientGrouping: string }[]>('/clients/lookup/groupings'),
    staleTime: 1000 * 60 * 30,
  });

/**
 * Distinct engagement groupings for the Engagements list filter. Uses vw_Engagement's
 * own ClientGrouping (which buckets C/S names into ranges like 'SN-ST'), matching the
 * ClientGrouping filter — the client-grouping lookup above uses single letters and does
 * NOT match here.
 */
export const useEngagementGroupings = () =>
  useQuery({
    queryKey: ['engagement-groupings'],
    queryFn: () => api.get<{ ClientGrouping: string }[]>('/engagements/lookup/groupings'),
    staleTime: 1000 * 60 * 30,
  });

/**
 * Clients for a grouping. all=false → existing master clients (New Sub picker);
 * all=true → every client master+sub (Bill To = Other picker). Fires only once a
 * grouping is chosen.
 */
export const useClientPicker = (grouping: string | undefined, all: boolean) =>
  useQuery({
    queryKey: ['client-picker', grouping ?? '', all],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (all) qs.set('all', 'true');
      return api.get<ClientPickerRow[]>(`/clients/lookup/ids?${qs.toString()}`);
    },
    enabled: !!grouping,
    staleTime: 1000 * 60 * 5,
  });

/**
 * Item 15: type-to-search client picker (replaces the "starts with" letter dropdown).
 * all=false -> master clients; all=true -> every client. Fires at 2+ characters.
 */
export const useClientSearch = (q: string, all: boolean) =>
  useQuery({
    queryKey: ['client-search', q.trim(), all],
    queryFn: () => {
      const qs = new URLSearchParams({ q: q.trim() });
      if (all) qs.set('all', 'true');
      return api.get<ClientPickerRow[]>(`/clients/lookup/ids?${qs.toString()}`);
    },
    enabled: q.trim().length >= 2,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });

export interface TemplateControl {
  ControlKey: string;
  ControlType: 'Deliverable' | 'OtherDetail';
  InputKind: 'checkbox' | 'dropdown' | 'date' | 'text';
  DisplayLabel: string;
  GroupName: string;
  LookupSource: string | null;
  SortOrder: number;
  IsEditable: boolean;
  IsRequired: boolean;
  IsDefaultChecked: boolean;
}

/** DB-driven template rules (deliverables + other-details) for a template. */
export const useTemplateControls = (serviceCategory?: string, templateName?: string) =>
  useQuery({
    queryKey: ['template-controls', serviceCategory ?? '', templateName ?? ''],
    queryFn: () =>
      api.get<TemplateControl[]>(
        `/engagements/templates/controls?serviceCategory=${encodeURIComponent(serviceCategory!)}&templateName=${encodeURIComponent(templateName!)}`,
      ),
    enabled: !!serviceCategory && !!templateName,
    staleTime: 1000 * 60 * 10,
  });

export const useEngagements = (grouping?: string, search?: string, year?: string) =>
  useQuery({
    queryKey: ['engagements', grouping ?? '', search ?? '', year ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (search) qs.set('search', search);
      if (year) qs.set('year', year);
      return api.get<Record<string, unknown>[]>(`/engagements?${qs.toString()}`);
    },
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  });

export const usePendingClients = () =>
  useQuery({ queryKey: ['pending', 'clients'], queryFn: () => api.get<Record<string, unknown>[]>('/pending/clients') });
export const usePendingEngagements = () =>
  useQuery({ queryKey: ['pending', 'engagements'], queryFn: () => api.get<Record<string, unknown>[]>('/pending/engagements') });

export const useCompletedClients = (search?: string) =>
  useQuery({
    queryKey: ['completed', 'clients', search ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>(`/completed/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });
export const useCompletedEngagements = (search?: string) =>
  useQuery({
    queryKey: ['completed', 'engagements', search ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>(`/completed/engagements${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

export const useCreateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post('/clients', body),
    onSuccess: () => {
      // Refresh the client grid AND the pickers (New Engagement / New Sub of
      // Existing Client), so a just-created client is searchable right away
      // instead of waiting for the picker cache to go stale.
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client-picker'] });
      qc.invalidateQueries({ queryKey: ['client-groupings'] });
    },
  });
};

export const useCreateEngagement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post('/engagements', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagements'] }),
  });
};

export const useUpdateClient = (clientId: string, subId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { body: unknown; clientType: string; opType: string }) =>
      api.put(`/clients/${clientId}/${subId}?clientType=${encodeURIComponent(args.clientType)}&opType=${args.opType}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
};

export const useUpdateEngagement = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { body: unknown; opType: string }) =>
      api.put(`/engagements/${id}?opType=${args.opType}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagements'] }),
  });
};

export const useEngagement = (id?: string) =>
  useQuery({ queryKey: ['engagement', id], queryFn: () => api.get<Record<string, unknown>>(`/engagements/${id}`), enabled: !!id });

export const useClient = (clientId?: string, subId?: string) =>
  useQuery({
    queryKey: ['client', clientId, subId],
    queryFn: () => api.get<Record<string, unknown>>(`/clients/${clientId}/${subId}`),
    enabled: !!clientId && !!subId,
  });

// ---- Request forms (Termination / Reactivation / ...) ----
export interface FormClientRow {
  ClientID: string;
  ClientName: string;
  ClientFullName: string;
  Terminated?: number;
}

/** Server-side client search for the request-form pickers ("starts with" + name). */
export const useFormClients = (scope: 'active' | 'all' | 'mass', q: string, grouping = '') =>
  useQuery({
    queryKey: ['form-clients', scope, q, grouping],
    queryFn: () =>
      api.get<FormClientRow[]>(
        `/forms/clients?scope=${scope}&q=${encodeURIComponent(q)}&grouping=${encodeURIComponent(grouping)}`,
      ),
    enabled: q.trim().length >= 2 || grouping.trim().length > 0,
    staleTime: 30_000,
  });

export interface ClientFormBody {
  clients: {
    clientId: string;
    clientName: string;
    parentClientId?: string;      // Termination: sub-client picked under its master
    newMasterClientId?: string;   // Termination: master leaving with subs staying
    newMasterClientName?: string;
  }[];
  effectiveDate: string;
  notes?: string;
  terminationReasonCode?: number;
  terminationReasonOther?: string;
  crunchafiDisable?: 'Yes' | 'No';
}

/** Item 15: a master client's active sub-clients (isMaster=false for anything else). */
export const useSubClients = (clientId: string) =>
  useQuery({
    queryKey: ['form-subclients', clientId],
    queryFn: () =>
      api.get<{ isMaster: boolean; subs: { ClientID: string; ClientName: string }[] }>(
        `/forms/clients/${encodeURIComponent(clientId)}/subclients`,
      ),
    enabled: !!clientId,
    staleTime: 60_000,
  });

const useSubmitClientForm = (path: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientFormBody) => api.post<{ submissionId: number }>(path, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};
export const useSubmitTermination = () => useSubmitClientForm('/forms/termination');
export const useSubmitReactivation = () => useSubmitClientForm('/forms/reactivation');

export interface EngagementRow {
  EngagementID: string;
  ServiceCategory: string;
  TemplateName: string;
  Year: number;
}
/** A client's engagements, for the Discontinuation form. */
export const useClientEngagements = (clientId?: string) =>
  useQuery({
    queryKey: ['client-engagements', clientId ?? ''],
    queryFn: () => api.get<EngagementRow[]>(`/forms/engagements?clientId=${encodeURIComponent(clientId ?? '')}`),
    enabled: !!clientId,
  });

/** Item 17: a row from the Discontinuation engagement search. */
export interface EngagementSearchRow extends EngagementRow {
  ClientID: string;
  ClientName: string;
  Requested: number; // on an earlier, unarchived Discontinuation request
}
/** Engagement search (Client, Engagement ID, Template or Service); fires at 2+ characters. */
export const useEngagementSearch = (q: string) =>
  useQuery({
    queryKey: ['engagement-search', q.trim()],
    queryFn: () => api.get<EngagementSearchRow[]>(`/forms/engagement-search?q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });

export interface DiscontinuationBody {
  clientId?: string;   // older one-client format; item 17 sends the client per engagement
  clientName?: string;
  engagements: {
    engagementId: string; serviceCategory?: string; templateName?: string;
    clientId?: string; clientName?: string;
  }[];
  effectiveDate: string;
  notes?: string;
}
/** Item 23: Help page feedback, emailed to Amanda and Tyler (copy to the sender). */
export interface FeedbackBody { type: 'Feedback' | 'Enhancement idea'; subject: string; details: string }
export const useSendFeedback = () =>
  useMutation({ mutationFn: (body: FeedbackBody) => api.post<{ sent: boolean }>('/forms/feedback', body) });

export const useSubmitDiscontinuation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DiscontinuationBody) => api.post<{ submissionId: number }>('/forms/discontinuation', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};

export interface SubmissionFilters {
  formType?: string;
  q?: string;
  from?: string;
  to?: string;
  /** '' = open only (default), 'only' = completed only, 'all' = both. */
  archived?: string;
}
/** Distinct form types that have submissions, for the filter dropdown. */
export const useSubmissionTypes = () =>
  useQuery({ queryKey: ['form-submission-types'], queryFn: () => api.get<string[]>('/forms/submission-types') });

export const useFormSubmissions = (filters: SubmissionFilters = {}) =>
  useQuery({
    queryKey: ['form-submissions', filters.formType ?? '', filters.q ?? '', filters.from ?? '', filters.to ?? '', filters.archived ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.formType) qs.set('formType', filters.formType);
      if (filters.q) qs.set('q', filters.q);
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      if (filters.archived) qs.set('archived', filters.archived);
      return api.get<Record<string, unknown>[]>(`/forms/submissions${qs.toString() ? `?${qs.toString()}` : ''}`);
    },
  });

// SS10: Submitted Forms checklist, complete / reopen and delete (admin).
export interface ChecklistRowSave {
  submissionId: number;
  detailType: 'Client' | 'Engagement' | 'Submission';
  detailId: number;
  maconomy?: string | null;
  constant?: string | null;
  clientManager?: string | null;
  returnManager?: string | null;
  clientProfile?: string | null;
  workflow?: string | null;
  cchEng?: string | null;
  el?: string | null;
  dateCompleted?: string | null;
}
/** One submitted request with its rows (View Form page). Shares the 'form-submissions'
 *  key prefix, so checklist saves, reopen and delete refresh it too. */
export const useFormSubmission = (id: number) =>
  useQuery({
    queryKey: ['form-submissions', 'one', id],
    queryFn: () => api.get<Record<string, unknown>[]>(`/forms/submissions/${id}`),
    enabled: Number.isFinite(id) && id > 0,
  });

export const useSaveChecklist = () => {
  const qc = useQueryClient();
  return useMutation({
    // completed = requests that moved to Completed with this save (all rows done).
    mutationFn: (rows: ChecklistRowSave[]) =>
      api.put<{ saved: number; completed: number[] }>('/forms/submissions/checklist', { rows }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};
export const useArchiveSubmission = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; archived: boolean }) =>
      api.post(`/forms/submissions/${a.id}/archive`, { archived: a.archived }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};
export const useDeleteSubmission = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/forms/submissions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-submissions'] });
      qc.invalidateQueries({ queryKey: ['form-submission-types'] });
    },
  });
};

// SS14: admin-editable Dashboard banner.
export interface DashboardBanner {
  enabled: boolean;
  message: string;
}
export const useDashboardBanner = () =>
  useQuery({ queryKey: ['dashboard-banner'], queryFn: () => api.get<DashboardBanner>('/settings/dashboard-banner') });
export const useUpdateDashboardBanner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: DashboardBanner) => api.put<DashboardBanner>('/settings/dashboard-banner', b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard-banner'] }),
  });
};
